import { useState } from 'react';
import { Flag, Check } from 'lucide-react';
import { cn } from '../../utils/cn';
import { SEVERITY_LABELS } from '../../utils/severity-labels';
import { createFeedback } from '../../services/api/adminFeedback';
import type { AdminFeedbackType, FeedbackSeverity } from '../../types/feedback';

const TYPE_OPTIONS: { value: AdminFeedbackType; label: string }[] = [
  { value: 'bad_answer', label: 'Fel svar' },
  { value: 'missing_knowledge', label: 'Saknar kunskap' },
  { value: 'wrong_intent', label: 'Fel intent' },
  { value: 'should_escalate', label: 'Borde eskaleras' },
];

const SEVERITIES: FeedbackSeverity[] = ['low', 'medium', 'high'];

interface Props {
  sessionId: string;
  userMessage: string;
  assistantAnswer: string;
  triggerLabel?: string;
  className?: string;
}

export const FlagButton = ({
  sessionId,
  userMessage,
  assistantAnswer,
  triggerLabel = 'Flagga svar',
  className,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [severity, setSeverity] = useState<FeedbackSeverity>('medium');
  const [type, setType] = useState<AdminFeedbackType>('bad_answer');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  if (done) {
    return (
      <span className={cn('inline-flex items-center gap-1 text-xs text-green-600', className)}>
        <Check className="h-3.5 w-3.5" /> Flaggad
      </span>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'inline-flex items-center gap-1 text-xs text-slate-400 transition hover:text-red-600',
          className,
        )}
      >
        <Flag className="h-3.5 w-3.5" /> {triggerLabel}
      </button>
    );
  }

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await createFeedback({
        conversationId: sessionId,
        userMessage: userMessage.trim() || '(okänd fråga)',
        assistantAnswer: assistantAnswer.trim() || '(tomt svar)',
        type,
        severity,
        notes: note.trim() || undefined,
        source: 'session',
      });
      setDone(true);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Kunde inte flagga');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={cn(
        'rounded-xl border border-slate-300 bg-white p-3 text-left shadow-sm',
        className,
      )}
    >
      <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-900">
        <Flag className="h-4 w-4 text-red-600" /> Flagga som dålig
      </div>

      <div className="mb-1 text-xs text-slate-500">Allvar</div>
      <div className="mb-3 flex gap-1.5">
        {SEVERITIES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setSeverity(s)}
            className={cn(
              'rounded-lg border px-2.5 py-1 text-xs transition',
              severity === s
                ? 'border-red-300 bg-red-50 text-red-700'
                : 'border-slate-300 text-slate-600 hover:bg-slate-50',
            )}
          >
            {SEVERITY_LABELS[s]}
          </button>
        ))}
      </div>

      <div className="mb-1 text-xs text-slate-500">Typ</div>
      <select
        value={type}
        onChange={(e) => setType(e.target.value as AdminFeedbackType)}
        className="mb-3 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
      >
        {TYPE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      <div className="mb-1 text-xs text-slate-500">Vad var fel? (valfritt)</div>
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Kort beskrivning…"
        className="mb-3 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
      />

      {error && <p className="mb-2 text-xs text-red-600">{error}</p>}

      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-slate-400">
          Sparas i Feedback · syns i veckorapporten
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-red-700 disabled:opacity-40"
          >
            <Flag className="h-3.5 w-3.5" /> {submitting ? 'Flaggar…' : 'Flagga'}
          </button>
        </div>
      </div>
    </div>
  );
};
